import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const CARD_WIDTH = 256;
const CARD_HEIGHT = 171;
const NEIGHBOURS = [[0, -1], [0, 1], [1, 0], [-1, 0], [1, 1], [-1, 1], [-1, -1], [1, -1]];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const applyDamping = (velocity, deltaTime) => {
  const dampingRate = 0.0028;
  return velocity * Math.exp(-dampingRate * deltaTime);
};

const smoothStep = (current, target, deltaTime, speed = 0.15) => 
  current + (target - current) * (1 - Math.exp(-speed * deltaTime));

const useViewportSize = () => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  
  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: 380 // Fixed height as requested
      });
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return size;
};

const useAnimationFrame = callback => {
  const requestRef = useRef(null);
  const previousTimeRef = useRef(undefined);
  
  const animate = useCallback(time => {
    if (previousTimeRef.current !== undefined) {
      const deltaTime = time - previousTimeRef.current;
      callback(deltaTime);
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  }, [callback]);
  
  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current !== null) cancelAnimationFrame(requestRef.current);
    };
  }, [animate]);
};

const Card = React.memo(({ descriptor, x, y, onClick }) => {
  const [opacity, setOpacity] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const imgRef = useRef(null);
  
  useEffect(() => {
    setOpacity(0);
    const img = new Image();
    img.src = descriptor.thumb_src;
    
    const fadeIn = () => {
      let start = null;
      const fade = t => {
        if (start === null) start = t;
        const p = Math.min(1, (t - start) / 300);
        setOpacity(p);
        if (p < 1) requestAnimationFrame(fade);
      };
      requestAnimationFrame(fade);
    };
    
    if (img.decode) {
      img.decode().then(fadeIn).catch(fadeIn);
    } else {
      img.onload = fadeIn;
    }
  }, [descriptor]);
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick && descriptor.id) {
      onClick(descriptor.id);
    }
  };
  
  return (
    <div 
      className="absolute overflow-hidden rounded-lg shadow-lg cursor-pointer group"
      style={{
        transform: `translate3d(${x}px, ${y}px, 0) ${isHovered ? 'scale(1.05)' : 'scale(1)'}`,
        willChange: 'transform',
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        maxWidth: CARD_WIDTH,
        maxHeight: CARD_HEIGHT,
        contain: 'layout style paint',
        transition: 'transform 0.3s ease'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <img 
        ref={imgRef}
        src={descriptor.thumb_src}
        alt={descriptor.title || 'Vehicle image'}
        className="absolute top-0 left-0 w-full h-full object-cover select-none"
        style={{
          opacity,
          transition: 'opacity 0.3s ease-out',
          imageRendering: 'crisp-edges',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)'
        }}
        loading="lazy"
        decoding="async"
      />
      {descriptor.title && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-3 group-hover:from-black/95">
          <p className="text-white text-sm font-semibold truncate">{descriptor.title}</p>
          {descriptor.price && (
            <p className="text-green-400 text-sm font-bold">${descriptor.price?.toLocaleString()}</p>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => 
  prevProps.descriptor === nextProps.descriptor && 
  prevProps.x === nextProps.x && 
  prevProps.y === nextProps.y
);

Card.displayName = 'Card';

const InfiniteDraggableGrid = ({ gallery }) => {
  const navigate = useNavigate();
  const viewportSize = useViewportSize();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [targetOffset, setTargetOffset] = useState({ x: 0, y: 0 });
  const [visibleCards, setVisibleCards] = useState([]);
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  
  const isDraggingRef = useRef(false);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const lastTimeRef = useRef(Date.now());
  const picksRef = useRef({});
  const containerRef = useRef(null);
  const momentumRef = useRef({ x: 0, y: 0 });
  const clickStartRef = useRef({ x: 0, y: 0 });
  const isDragMoveRef = useRef(false);
  
  // Auto-scroll effect
  useEffect(() => {
    if (!isDraggingRef.current) {
      const autoScrollInterval = setInterval(() => {
        if (!isDraggingRef.current && Math.abs(momentumRef.current.x) < 0.1) {
          momentumRef.current.x = 5; // Gentle auto-scroll
        }
      }, 3000);
      return () => clearInterval(autoScrollInterval);
    }
  }, []);
  
  useAnimationFrame(useCallback(deltaTime => {
    if (!isDraggingRef.current) {
      momentumRef.current.x = applyDamping(momentumRef.current.x, deltaTime);
      momentumRef.current.y = applyDamping(momentumRef.current.y, deltaTime);
      
      if (Math.abs(momentumRef.current.x) < 0.01) momentumRef.current.x = 0;
      if (Math.abs(momentumRef.current.y) < 0.01) momentumRef.current.y = 0;
      
      setTargetOffset(prev => ({
        x: prev.x + momentumRef.current.x,
        y: prev.y + momentumRef.current.y
      }));
    }
    
    setOffset(prev => ({
      x: smoothStep(prev.x, targetOffset.x, deltaTime, isDraggingRef.current ? 0.4 : 0.18),
      y: smoothStep(prev.y, targetOffset.y, deltaTime, isDraggingRef.current ? 0.4 : 0.18)
    }));
  }, [targetOffset]));
  
  const handleCardClick = useCallback((vehicleId) => {
    // Only navigate if we didn't drag
    if (!isDragMoveRef.current && vehicleId) {
      navigate(`/vehicle/${vehicleId}`);
    }
  }, [navigate]);

  const handleDragStart = useCallback(e => {
    isDraggingRef.current = true;
    isDragMoveRef.current = false;
    momentumRef.current = { x: 0, y: 0 };
    
    const point = 'touches' in e ? e.touches[0] : e;
    lastPositionRef.current = { x: point.clientX, y: point.clientY };
    clickStartRef.current = { x: point.clientX, y: point.clientY };
    lastTimeRef.current = Date.now();
    
    if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
  }, []);
  
  const handleDragMove = useCallback(e => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    
    const point = e instanceof TouchEvent ? e.touches[0] : e;
    const currentTime = Date.now();
    const timeDelta = currentTime - lastTimeRef.current;
    
    const deltaX = point.clientX - lastPositionRef.current.x;
    const deltaY = point.clientY - lastPositionRef.current.y;
    
    // Detect if this is a drag (moved more than 5 pixels)
    const totalMove = Math.abs(point.clientX - clickStartRef.current.x) + 
                      Math.abs(point.clientY - clickStartRef.current.y);
    if (totalMove > 5) {
      isDragMoveRef.current = true;
    }
    
    if (timeDelta > 0) {
      const vx = deltaX / timeDelta * 16;
      const vy = deltaY / timeDelta * 16;
      setVelocity(prev => ({
        x: prev.x * 0.5 + vx * 0.5,
        y: prev.y * 0.5 + vy * 0.5
      }));
    }
    
    lastPositionRef.current = { x: point.clientX, y: point.clientY };
    lastTimeRef.current = currentTime;
    
    setTargetOffset(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
  }, []);
  
  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    
    momentumRef.current = {
      x: clamp(velocity.x, -30, 30),
      y: clamp(velocity.y, -30, 30)
    };
    
    setVelocity({ x: 0, y: 0 });
    if (containerRef.current) containerRef.current.style.cursor = 'grab';
  }, [velocity]);
  
  useEffect(() => {
    const move = e => handleDragMove(e);
    const end = () => handleDragEnd();
    
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('mouseup', end);
    window.addEventListener('touchend', end);
    
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchend', end);
    };
  }, [handleDragMove, handleDragEnd]);
  
  const visibleCardsData = useMemo(() => {
    if (!gallery.length || viewportSize.width === 0) return [];
    
    const getGalleryDescriptor = index => gallery[Math.abs(index % gallery.length)];
    
    const getRandomSafe = (col, row) => {
      let pick;
      let tries = 0;
      while (pick === undefined) {
        const rnd = Math.floor(Math.random() * gallery.length);
        const item = getGalleryDescriptor(rnd);
        let isSafe = true;
        
        for (const offsets of NEIGHBOURS) {
          const key = `${col + offsets[0]}:${row + offsets[1]}`;
          if (picksRef.current[key] === item) {
            isSafe = false;
            break;
          }
        }
        
        if (tries++ > 20 || isSafe) {
          pick = item;
        }
      }
      return pick;
    };
    
    const getRandomDescriptor = (col, row) => {
      const key = `${col}:${row}`;
      if (!picksRef.current[key]) {
        picksRef.current[key] = getRandomSafe(col, row);
      }
      return picksRef.current[key];
    };
    
    const getCardPos = (col, row) => {
      const x = col * CARD_WIDTH + Math.round(offset.x) % CARD_WIDTH - CARD_WIDTH;
      const y = row * CARD_HEIGHT + Math.round(offset.y) % CARD_HEIGHT - CARD_HEIGHT;
      return [x, y];
    };
    
    const isVisible = (x, y) => {
      const buffer = 100;
      return x + CARD_WIDTH > -buffer && 
             y + CARD_HEIGHT > -buffer && 
             x < viewportSize.width + buffer && 
             y < 380 + buffer;
    };
    
    const viewCols = Math.ceil(viewportSize.width / CARD_WIDTH) + 4;
    const viewRows = Math.ceil(380 / CARD_HEIGHT) + 4;
    const colOffset = Math.floor(offset.x / CARD_WIDTH) * -1;
    const rowOffset = Math.floor(offset.y / CARD_HEIGHT) * -1;
    
    const newVisibleCards = [];
    for (let row = -2; row < viewRows; row++) {
      for (let col = -2; col < viewCols; col++) {
        const tCol = colOffset + col;
        const tRow = rowOffset + row;
        const desc = getRandomDescriptor(tCol, tRow);
        const [x, y] = getCardPos(col, row);
        
        if (isVisible(x, y)) {
          newVisibleCards.push({
            key: `${tCol}:${tRow}`,
            descriptor: desc,
            x,
            y
          });
        }
      }
    }
    
    return newVisibleCards;
  }, [gallery, offset, viewportSize]);
  
  useEffect(() => {
    setVisibleCards(visibleCardsData);
  }, [visibleCardsData]);
  
  return (
    <div
      ref={containerRef}
      className="w-full h-full select-none cursor-grab overflow-hidden relative"
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        touchAction: 'none',
        height: '380px',
        width: '100%',
        margin: 0,
        padding: 0
      }}
    >
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          width: '100%',
          height: '100%'
        }}
      >
        {visibleCards.map(card => (
          <Card 
            key={card.key}
            descriptor={card.descriptor}
            x={card.x}
            y={card.y}
            onClick={handleCardClick}
          />
        ))}
      </div>
      
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
        <div className="text-center">
          <h2 className="text-white text-4xl md:text-5xl font-bold tracking-wider opacity-90 drop-shadow-2xl mb-2">
          </h2>
          <p className="text-red/70 text-lg">Drag to browse • Click for details</p>
        </div>
      </div>
    </div>
  );
};

export default InfiniteDraggableGrid;
